import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../App';
import { useNavigate } from 'react-router-dom';
import { X, Copy, Check, Gift, Calendar } from 'lucide-react';
import { toast } from 'sonner';
import Footer from './Footer';

// Custom scrollbar styles
const scrollbarStyles = `
  .custom-scrollbar-thin::-webkit-scrollbar {
    width: 4px;
  }
  
  .custom-scrollbar-thin::-webkit-scrollbar-track {
    background: transparent;
  }
  
  .custom-scrollbar-thin::-webkit-scrollbar-thumb {
    background: rgba(139, 92, 246, 0.5);
    border-radius: 20px;
  }
  
  .custom-scrollbar-thin::-webkit-scrollbar-thumb:hover {
    background: rgba(139, 92, 246, 0.7);
  }
`;

const API = process.env.REACT_APP_BACKEND_URL;

import SEOHead from './SEOHead';

const StudentOffers = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  
  const [offers, setOffers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [loading, setLoading] = useState(true);
  const [selectedOffer, setSelectedOffer] = useState(null);
  const [copiedCodes, setCopiedCodes] = useState(new Set());

  useEffect(() => {
    if (!user) {
      toast.error('Please login to view offers');
      navigate('/');
      return;
    }
    fetchCategories();
    fetchOffers();
  }, [user]);

  useEffect(() => {
    if (user) {
      fetchOffers();
    }
  }, [selectedCategory]);

  const fetchCategories = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/api/offers/categories`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        setCategories(data.categories);
      }
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  };

  const fetchOffers = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const url = selectedCategory === 'all' 
        ? `${API}/api/offers/public`
        : `${API}/api/offers/public?category=${selectedCategory}`;
      
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        setOffers(data.offers);
      }
    } catch (error) {
      console.error('Failed to fetch offers:', error);
      toast.error('Failed to load offers');
    } finally {
      setLoading(false);
    }
  };

  const handleCopyCode = async (offer, e) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(offer.coupon_code);
      setCopiedCodes(prev => new Set([...prev, offer.id]));
      
      // Track copy
      const token = localStorage.getItem('token');
      await fetch(`${API}/api/offers/copy/${offer.id}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      toast.success('Code copied!');
      setTimeout(() => {
        setCopiedCodes(prev => {
          const newSet = new Set(prev);
          newSet.delete(offer.id);
          return newSet;
        });
      }, 2000);
    } catch (error) {
      toast.error('Failed to copy code');
    }
  };

  const openOfferModal = async (offer) => {
    setSelectedOffer(offer);
    
    // Track view
    try {
      const token = localStorage.getItem('token');
      await fetch(`${API}/api/offers/view/${offer.id}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
    } catch (error) {
      console.error('Failed to track view:', error);
    }
  };

  const closeModal = () => {
    setSelectedOffer(null);
  };

  const getCategoryName = (categoryId) => {
    const category = categories.find(c => c.id === categoryId);
    return category ? `${category.emoji} ${category.name}` : categoryId;
  };

  const trackURLClick = async (offerId, url) => {
    try {
      const token = localStorage.getItem('token');
      await fetch(`${API}/api/offers/click-url/${offerId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ url })
      });
    } catch (error) {
      console.error('Failed to track URL click:', error);
    }
  };

  const makeLinksClickable = (text, offerId) => {
    if (!text) return '';
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    return text.split(urlRegex).map((part, i) => 
      urlRegex.test(part) ? (
        <a 
          key={i} 
          href={part} 
          target="_blank" 
          rel="noopener noreferrer" 
          className="text-indigo-400 hover:text-indigo-300 underline"
          onClick={() => trackURLClick(offerId, part)}
        >
          {part}
        </a>
      ) : part
    );
  };

  const isExpired = (expiryDate) => {
    return new Date(expiryDate) < new Date();
  };

  return (
    <>
      <style>{scrollbarStyles}</style>
      <SEOHead
        title="Student Offers & Exclusive Deals"
        description="Exclusive discounts, vouchers and deals for students on Nerd Nation. Save on software, courses and study tools curated for ambitious students."
        path="/student-offers"
      />
      <div className="min-h-screen bg-[#0a0a0f] text-white" style={{ fontFamily: 'DM Sans, sans-serif' }}>
      {/* Hero Section */}
      <div className="bg-gradient-to-br from-indigo-900/40 via-purple-900/30 to-[#0a0a0f] pt-12 sm:pt-20 pb-8 sm:pb-12 px-4">
        <div className="max-w-6xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-indigo-500/20 px-4 py-2 rounded-full mb-4">
            <Gift className="w-5 h-5 text-indigo-400" />
            <span className="text-sm font-medium text-indigo-300" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Student Offers</span>
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold mb-4 text-white" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            Student Offer Zone
          </h1>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto">
            Save big with verified student deals on software, courses, food, travel & more
          </p>
        </div>
      </div>

      {/* Category Filter */}
      <div className="max-w-6xl mx-auto px-4 py-3 sm:py-6">
        <div className="flex flex-wrap gap-2 justify-center">
          <button
            onClick={() => setSelectedCategory('all')}
            className={`px-3 py-1.5 sm:px-4 sm:py-2 rounded-full text-xs sm:text-sm font-medium transition-all ${
              selectedCategory === 'all'
                ? 'bg-gradient-to-r from-indigo-500 to-purple-500 text-white'
                : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
            }`}
          >
            All Offers
          </button>
          {categories.map(cat => (
            <button
              key={cat.id}
              onClick={() => setSelectedCategory(cat.id)}
              className={`px-3 py-1.5 sm:px-4 sm:py-2 rounded-full text-xs sm:text-sm font-medium transition-all ${
                selectedCategory === cat.id
                  ? 'bg-gradient-to-r from-indigo-500 to-purple-500 text-white'
                  : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
              }`}
            >
              {cat.emoji} {cat.name}
            </button>
          ))}
        </div>
      </div>

      {/* Offers Grid */}
      <div className="max-w-6xl mx-auto px-4 pb-20">
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3, 4, 5, 6].map(i => (
              <div key={i} className="bg-gray-800/50 rounded-xl h-96 animate-pulse" />
            ))}
          </div>
        ) : offers.length === 0 ? (
          <div className="text-center py-20">
            <Gift className="w-16 h-16 text-gray-600 mx-auto mb-4" />
            <p className="text-gray-400 text-lg">No offers available in this category</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {offers.map(offer => (
              <div
                key={offer.id}
                onClick={() => openOfferModal(offer)}
                className="bg-gray-900/80 rounded-xl overflow-hidden cursor-pointer hover:transform hover:scale-105 transition-all duration-300 border border-gray-800 hover:border-indigo-500/50"
              >
                {/* Offer Image */}
                <div className="relative h-48 bg-gray-800">
                  <img
                    src={offer.image_base64}
                    alt={offer.title}
                    className="w-full h-full object-cover" loading="lazy" decoding="async"/>
                  {/* Discount Badge */}
                  <div className="absolute top-3 right-3 bg-gradient-to-r from-indigo-500 to-purple-500 px-3 py-1 rounded-full">
                    <span className="text-white text-sm font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                      {offer.discount_label}
                    </span>
                  </div>
                  {/* Expired Badge */}
                  {isExpired(offer.expiry_date) && (
                    <div className="absolute inset-0 bg-black/70 flex items-center justify-center">
                      <span className="text-red-400 text-lg font-bold">EXPIRED</span>
                    </div>
                  )}
                </div>

                {/* Content */}
                <div className="p-4">
                  <p className="text-gray-400 text-xs font-medium mb-1">{offer.brand}</p>
                  <h3 className="text-white font-bold text-lg mb-2 line-clamp-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                    {offer.title}
                  </h3>
                  
                  {/* Category Tag */}
                  <div className="inline-block bg-gray-800 px-2 py-1 rounded text-xs text-gray-300 mb-3">
                    {getCategoryName(offer.category)}
                  </div>

                  {/* Coupon Code */}
                  <div className="border-2 border-dashed border-gray-700 rounded-lg p-3 mb-3">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <p className="text-xs text-gray-400 mb-1">Coupon Code</p>
                        <p className="text-white font-mono font-bold tracking-wider">{offer.coupon_code}</p>
                      </div>
                      <button
                        onClick={(e) => handleCopyCode(offer, e)}
                        className={`ml-2 px-4 py-2 rounded-lg font-medium text-sm transition-all ${
                          copiedCodes.has(offer.id)
                            ? 'bg-green-500 text-white'
                            : 'bg-gradient-to-r from-indigo-500 to-purple-500 text-white hover:shadow-lg'
                        }`}
                      >
                        {copiedCodes.has(offer.id) ? (
                          <span className="flex items-center gap-1">
                            <Check className="w-4 h-4" /> Done
                          </span>
                        ) : (
                          <span className="flex items-center gap-1">
                            <Copy className="w-4 h-4" /> Copy
                          </span>
                        )}
                      </button>
                    </div>
                  </div>

                  {/* Expiry Date */}
                  <div className="flex items-center gap-2 text-xs text-gray-400">
                    <Calendar className="w-3 h-3" />
                    <span>Expires: {new Date(offer.expiry_date).toLocaleDateString()}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Bottom Sheet Modal */}
      {selectedOffer && (
        <div 
          className="fixed inset-0 bg-black/80 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4"
          onClick={closeModal}
        >
          <div 
            className="bg-[#0a0a0f] w-full sm:max-w-2xl sm:rounded-2xl rounded-t-3xl overflow-hidden max-h-[90vh] overflow-y-auto custom-scrollbar-thin"
            onClick={(e) => e.stopPropagation()}
            style={{
              scrollbarWidth: 'thin',
              scrollbarColor: 'rgba(139, 92, 246, 0.5) transparent'
            }}
          >
            {/* Drag Pill */}
            <div className="sm:hidden flex justify-center py-2 bg-gray-900">
              <div className="w-12 h-1 bg-gray-700 rounded-full" />
            </div>

            {/* Image */}
            <div className="relative">
              <img
                src={selectedOffer.image_base64}
                alt={selectedOffer.title}
                className="w-full h-64 object-cover" loading="lazy" decoding="async"/>
              <button
                onClick={closeModal}
                className="absolute top-4 right-4 bg-white/20 hover:bg-white/30 rounded-full p-2 transition-colors backdrop-blur-sm shadow-lg shadow-white/50"
                style={{ boxShadow: '0 0 20px rgba(255, 255, 255, 0.5)' }}
              >
                <X className="w-6 h-6 text-white font-bold" strokeWidth={3} />
              </button>
            </div>

            {/* Content */}
            <div className="p-6">
              <p className="text-gray-400 text-sm font-medium mb-2">{selectedOffer.brand}</p>
              <h2 className="text-2xl font-bold mb-4 text-white" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                {selectedOffer.title}
              </h2>

              {/* Discount Badge */}
              <div className="inline-block bg-gradient-to-r from-indigo-500 to-purple-500 px-4 py-2 rounded-lg mb-4">
                <span className="text-white font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                  {selectedOffer.discount_label}
                </span>
              </div>

              {/* Expiry */}
              <div className="flex items-center gap-2 text-sm text-gray-400 mb-6">
                <Calendar className="w-4 h-4" />
                <span>Valid until: {new Date(selectedOffer.expiry_date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
              </div>

              {/* Coupon Code */}
              <div className="border-2 border-dashed border-gray-700 rounded-lg p-6 mb-6 text-center">
                <p className="text-gray-400 text-sm mb-2">Coupon Code</p>
                <p className="text-white font-mono font-bold text-2xl tracking-widest mb-4">{selectedOffer.coupon_code}</p>
                <button
                  onClick={(e) => handleCopyCode(selectedOffer, e)}
                  className={`w-full py-3 rounded-lg font-bold transition-all ${
                    copiedCodes.has(selectedOffer.id)
                      ? 'bg-green-500 text-white'
                      : 'bg-gradient-to-r from-indigo-500 to-purple-500 text-white hover:shadow-xl'
                  }`}
                  style={{ fontFamily: 'Space Grotesk, sans-serif' }}
                >
                  {copiedCodes.has(selectedOffer.id) ? (
                    <span className="flex items-center justify-center gap-2">
                      <Check className="w-5 h-5" /> Code Copied!
                    </span>
                  ) : (
                    <span className="flex items-center justify-center gap-2">
                      <Copy className="w-5 h-5" /> Copy Code
                    </span>
                  )}
                </button>
              </div>

              {/* Instructions */}
              {selectedOffer.instructions && (
                <div className="bg-gray-900/50 rounded-lg p-4">
                  <h3 className="text-white font-bold mb-3" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                    How to Avail This Offer
                  </h3>
                  <div className="space-y-2">
                    {selectedOffer.instructions.split('\n').filter(line => line.trim()).map((line, idx) => (
                      <div key={idx} className="flex gap-3">
                        <span className="text-indigo-400 font-bold flex-shrink-0">{idx + 1}.</span>
                        <p className="text-gray-300 text-sm leading-relaxed">
                          {makeLinksClickable(line, selectedOffer.id)}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
    </>
  );
};

export default StudentOffers;
